<template>
    <div class="seatMap">
        <div class="screen">Screen</div>

        <span
            v-for="(nSeats, iSection) in theater.sections"
            class="section"
            v-if="nSeats"
        >
            <span
                class="rowId"
                :style="'flex-basis: ' + (((iSection - 1) % 3 === 0) ? 16.67 : 20) + '%;'"
            >{{ getSeat(iSection, 1).section }}</span>
            <span
                :style="'flex-basis: ' + (((iSection - 1) % 3 === 0) ? 8.33 : 10) + '%;'"
                v-if="nSeats < (((iSection - 1) % 3 === 0) ? 5 : 4)"
            >&nbsp;</span>
            <one-seat
                v-for="iSeat in nSeats"
                :seat="getSeat(iSection, iSeat)"
                :original-selections="selections"
                :key="iSection + '-' + iSeat"
                @change="updateSeatSelection(iSection, iSeat, $event)"
                :style="'flex-basis: ' + (((iSection - 1) % 3 === 0) ? 16.67 : 20) + '%;'"
            />
        </span>
    </div>
</template>

<script>
import OneSeat from './OneSeat';
import _groupBy from 'lodash/groupBy';

export default {
    components: {
        OneSeat,
    },

    props: {
        seats: {
            type: Array,
            default: () => [],
        },
        nTickets: {
            type: [String, Number],
        },
        selections: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            mySeats: [],
        };
    },

    computed: {
        couchesTaken() {
            let myCouches = this.seats
                // just couch seats I have selected
                .filter(seat => !!seat.seat_supergroup_id && !seat.is_wheelchair_space && this.selections.includes(seat.hashid))
                // keep only the supergroup_id (couchID)
                .map(seat => seat.seat_supergroup_id)
                // only unique supergroup_ids (couch IDs)
                .filter((value, index, self) => self.indexOf(value) === index)
            ;
            return this.seats
                // just taken couch seats
                .filter(seat => seat.taken && !!seat.seat_supergroup_id && !seat.is_wheelchair_space)
                // keep only the supergroup_id (couch ID)
                .map(seat => seat.seat_supergroup_id)
                // only unique supergroup_ids (couch IDs)
                .filter((value, index, self) => self.indexOf(value) === index)
                // and I'm not sitting in it already
                .filter(couch => !myCouches.includes(couch))
            ;
        },
    },

    watch: {
        selections: {
            immediate: true,
            handler() {
                this.reset();
            },
        },
        nTickets() {
            this.checkSeatChoiceValid();
        },
    },

    methods: {
        reset() {
            this.mySeats.splice(0, this.mySeats.length, ...this.selections);
        },

        getSeat(iSection, iSeat) {
            let seatIndex = this.getSeatIndex(iSection, iSeat);
            let seat = Object.assign({}, this.seats[seatIndex]);
            seat.isCouch = !!seat.seat_supergroup_id && !seat.is_wheelchair_space;
            seat.image = this.getSeatImage(seat);
            seat.selected = this.mySeats.includes(seat.hashid);
            seat.available = seat.available
                && !(seat.taken && !this.selections.includes(seat.hashid))
                && !this.couchesTaken.includes(seat.seat_supergroup_id)
            ;

            return seat;
        },
        getSeatIndex(iSection, iSeat) {
            return this.theater.sections.slice(0, iSection).reduce((sum, elm) => sum + elm, 0) + iSeat - 1;
        },
        getSeatImage(seat) {
            let filename;
            if (seat.is_wheelchair_space) {
                filename = 'wheelchair-1';
            } else if (seat.isCouch) {
                filename = 'couch-4';
                // don't change this to ===
                let idx = seat.section_seq - (seat.section == 4 ? 2 : 1);
                filename += ['d', 'c', 'b', 'a'][idx];
            } else {
                filename = 'seats-1';
            }

            if (seat.taken && !this.selections.includes(seat.hashid)) {
                filename = filename + '-filled';
            } else if (!seat.available) {
                filename = filename + '-blocked';
            }
            return '/img/' + filename + '.png';
        },

        // this wasn't working as a computed (seatChoiceValid) so moved to methods and added watches
        checkSeatChoiceValid() {
            if (this.mySeats.length < Number(this.nTickets)) {
                this.$emit('invalid', 'Select enough seats for your party.');
                return;
            }

            if (this.mySeats.length > Number(this.nTickets)) {
                this.$emit('invalid', 'Too many seats selected.');
                return;
            }

            let couchSeats = this.seats.filter(seat => this.mySeats.includes(seat.hashid) && !!seat.seat_supergroup_id);
            let couches = _groupBy(couchSeats, 'seat_supergroup_id');
            let couchesWith1 = Object.values(couches)
                .filter(seats => seats.length === 1)
                .filter(seats => !seats[0].is_wheelchair_space)
            ;
            if (couchesWith1.length > 0) {
                this.$emit('invalid', 'Two or more people to reserve a couch.');
                return;
            }

            this.$emit('valid');
        },

        updateSeatSelection(iSection, iSeat, selected) {
            let hashid = this.seats[this.getSeatIndex(iSection, iSeat)].hashid;
            let i = this.mySeats.indexOf(hashid);
            if (selected) {
                if (i < 0) {
                    this.mySeats.push(hashid);
                }
            } else {
                if (i >= 0) {
                    this.mySeats.splice(i, 1);
                }
            }
            this.$emit('change', this.mySeats);
            this.checkSeatChoiceValid();
        },
    },

    created() {
        this.theater = {
            sections: [
                4, 4, 4,
                4, 5, 4,
                4, 5, 4,
                4, 5, 4,
                4, 5, 4,
                4, 5, 4,
                4, 5, 4,
                4, 4, 4,
                4, 4, 4,
                3, null, 3,
            ],
        };
    },
};
</script>

<style lang="scss">
.seatMap {
    border: 4px solid #d88d34;
    border-radius: 4px;
    margin: 10px 0;

    width: 100%;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    .screen {
        background-color: #eee;
        text-align: center;
        padding: 10px 0;
        flex: 0 0 100%;
        letter-spacing: 10px;
        font-weight: bold;
        text-transform: uppercase;
    }

    .rowId {
        //position: absolute;
        //top: 4px;
        //left: 1px;
        font-weight: bold;
        padding: 4px 0;
        //background-color: #7f161b;
        background-color: #3298dc;
        color: #ebdaad;
        border-radius: 50%;
        width: 1.2em;
        line-height: 1;
        //vertical-align: top;
        margin: 1px 0 0 0;
        //font-size: 75%;
        font-size: clamp(10pt, 3vw, 16pt);
    }

    .section {
        padding: 2px 0;
        text-align: center;
        border: 1px solid #aaa;
        position: relative;
        //flex: 0 0 38%;
        flex: 0 0 calc(100% / 16 * 6);
        display: flex;
        flex-wrap: nowrap;
        align-items: center;

        &:nth-of-type(3n), &:nth-of-type(3n+1), &:last-of-type {
            //flex: 0 0 31%;
            flex: 0 0 calc(100% / 16 * 5);
        }

        span {
            display: inline-block;
            margin: 0;

            label {
                display: inline-block;
            }
        }

        span label:after {
            text-align: center;
            color: rgba(128, 0, 0, 0.7);
            font-size: 1.9vw;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%,-50%);
            font-weight: bold;
            white-space: nowrap;
        }

        img {
            //height: 20px;
            //height: calc(100vw / 18.5);
            margin-top: 3px;
        }
        img.couch {
            //height: 28px;
            //height: calc(100vw / 13.15);
            margin-top: 0;
        }
        img.wheelchair {
            //height: 23px;
            //height: calc(100vw / 16);
            margin: 0 0 -2px 0;
            padding: 5px 2px 2px 2px;
            box-sizing: content-box;
        }
    }

    input:checked + label {
        background-color: #d88d34;
    }
    input {
        display: none;
    }
}
</style>